import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { ProblemDetailsAlert } from "@/components/ProblemDetailsAlert";
import { useApiAction } from "@/hooks/useApiAction";

type OrganisationRole = components["schemas"]["OrganisationRole"];
type UpdateUserRoleResponse = components["schemas"]["UpdateUserRoleResponse"];

const ROLE_OPTIONS: OrganisationRole[] = ["Member", "Admin", "Owner"];

interface EditUserRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  organisationId: string;
  organisationName: string;
  currentRole: OrganisationRole;
  onUpdated: (newRole: OrganisationRole) => void;
}

export function EditUserRoleDialog({
  open,
  onOpenChange,
  userId,
  organisationId,
  organisationName,
  currentRole,
  onUpdated,
}: EditUserRoleDialogProps) {
  const [role, setRole] = useState<OrganisationRole>(currentRole);

  const action = useApiAction(
    (nextRole: OrganisationRole): Promise<{
      data?: UpdateUserRoleResponse;
      error?: components["schemas"]["ProblemDetails"];
    }> =>
      api.PATCH("/admin/users/{userId}/organisations/{organisationId}", {
        params: { path: { userId, organisationId } },
        body: { role: nextRole },
      }),
  );

  useEffect(() => {
    if (open) {
      setRole(currentRole);
      action.reset();
    }
    // intentionally only re-run on open / currentRole change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentRole]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (role === currentRole) {
      onOpenChange(false);
      return;
    }
    const result = await action.run(role);
    if (result) {
      onUpdated(result.role ?? role);
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit role</DialogTitle>
            <DialogDescription>
              Update this user's role in{" "}
              <span className="text-foreground font-medium">{organisationName}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <div className="flex flex-wrap gap-2">
                {ROLE_OPTIONS.map((r) => {
                  const selected = role === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(r)}
                      disabled={action.pending}
                      aria-pressed={selected}
                      className="rounded-full focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Badge
                        variant={selected ? "default" : "outline"}
                        className={cn(
                          "cursor-pointer px-3 py-1 text-xs",
                          !selected && "hover:bg-accent",
                        )}
                      >
                        {r}
                      </Badge>
                    </button>
                  );
                })}
              </div>
              <p className="text-muted-foreground text-xs">
                Each organisation must retain at least one Owner.
              </p>
            </div>

            <ProblemDetailsAlert
              problem={action.error}
              fallback="Failed to update role."
            />
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={action.pending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={action.pending || role === currentRole}>
              {action.pending && <Spinner className="size-4" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
